import React, { useEffect, useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { MultiSelect } from 'primereact/multiselect';
import { Checkbox } from 'primereact/checkbox';
import { Message } from 'primereact/message';
import { attendanceAPI, absenceAPI } from '../services/api';
import { Worker, Absence } from '../types';
import { showCustomAlert } from './CustomAlert';

type MonthlyAllResponse = {
  period: { year: number; month: number; daysInMonth: number };
  reports: Array<{
    worker: {
      _id: string;
      firstName: string;
      lastName: string;
      employeeId?: string;
      position: string;
      onSickLeave?: boolean;
      isActive?: boolean;
    };
    summary: {
      totalWorkDays: number;
      totalSickLeaveDays: number;
      totalAnnualLeaveDays: number;
      totalPaidLeaveDays: number;
      totalUnpaidLeaveDays: number;
      totalSlavaDays: number;
      totalHolidayDays: number;
      totalHoursWorked: number;
      totalOvertimeHours: number;
    };
  }>;
};

type MonthlyWorkerResponse = {
  worker: { _id: string; firstName: string; lastName: string; employeeId?: string; position: string };
  period: { year: number; month: number; daysInMonth: number };
  summary: MonthlyAllResponse['reports'][number]['summary'];
  records: Array<{
    _id: string;
    date: string;
    hoursWorked: number;
    overtimeHours: number;
    isAnnualLeave: boolean;
    isSickLeave: boolean;
    isSlava: boolean;
    notes?: string;
  }>;
  sickLeaves: any[];
  annualLeaves: Array<{
    _id: string;
    startDate: string;
    endDate: string;
    numberOfDays: number;
    leaveType: 'old' | 'new';
    notes?: string;
  }>;
};

type PeriodType = 'monthly' | 'custom';

const monthOptions = [
  { label: 'Januar', value: 1 },
  { label: 'Februar', value: 2 },
  { label: 'Mart', value: 3 },
  { label: 'April', value: 4 },
  { label: 'Maj', value: 5 },
  { label: 'Jun', value: 6 },
  { label: 'Jul', value: 7 },
  { label: 'Avgust', value: 8 },
  { label: 'Septembar', value: 9 },
  { label: 'Oktobar', value: 10 },
  { label: 'Novembar', value: 11 },
  { label: 'Decembar', value: 12 }
];

function formatDateSerbian(dateInput?: string | Date | null): string {
  if (!dateInput) return '—';
  const d = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  if (Number.isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}. ${mm}. ${yyyy}.`;
}

function toYmd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function downloadTextFile(filename: string, content: string, mime = 'text/csv;charset=utf-8') {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function csvEscape(v: any): string {
  const s = String(v ?? '');
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

const dataTypeOptions = [
  { label: 'Svi podaci', value: 'all' },
  { label: 'Radni dani', value: 'work' },
  { label: 'Bolovanje', value: 'sick' },
  { label: 'Godišnji odmor', value: 'annual' },
  { label: 'Plaćeno odsustvo', value: 'paid' },
  { label: 'Neplaćeno odsustvo', value: 'unpaid' },
  { label: 'Državni praznici', value: 'holiday' },
  { label: 'Slava', value: 'slava' },
  { label: 'Prekovremeni', value: 'overtime' }
];

const AttendanceReports: React.FC = () => {
  const now = useMemo(() => new Date(), []);
  const [month, setMonth] = useState<number>(now.getMonth() + 1);
  const [year, setYear] = useState<number>(now.getFullYear());

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<MonthlyAllResponse | null>(null);
  const [workers, setWorkers] = useState<Worker[]>([]);

  const [detailVisible, setDetailVisible] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<MonthlyWorkerResponse | null>(null);

  const [advancedVisible, setAdvancedVisible] = useState(false);
  const [periodType, setPeriodType] = useState<PeriodType>('monthly');
  const [customFrom, setCustomFrom] = useState<Date>(new Date());
  const [customTo, setCustomTo] = useState<Date>(new Date());
  const [exportTypes, setExportTypes] = useState<string[]>(['all']);
  const [allWorkers, setAllWorkers] = useState(true);
  const [selectedWorkers, setSelectedWorkers] = useState<string[]>([]);
  const [exportError, setExportError] = useState('');

  const yearOptions = useMemo(() => {
    const base: Array<{ label: string; value: number }> = [];
    for (let y = now.getFullYear() + 1; y >= now.getFullYear() - 5; y--) base.push({ label: String(y), value: y });
    return base;
  }, [now]);

  const refresh = async () => {
    setLoading(true);
    try {
      const [monthlyAll, allWorkersList] = await Promise.all([
        attendanceAPI.getMonthlyReportAll(year, month) as Promise<MonthlyAllResponse>,
        attendanceAPI.getWorkers({ isActive: null })
      ]);
      setData(monthlyAll);
      setWorkers(allWorkersList);
    } catch (e: any) {
      console.error('Error loading monthly reports:', e);
      showCustomAlert.error(e?.response?.data?.message || 'Greška pri učitavanju izveštaja');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, year]);

  const openDetails = async (workerId: string) => {
    setDetailVisible(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const rep = await attendanceAPI.getMonthlyReportForWorker(workerId, year, month);
      setDetail(rep);
    } catch (e: any) {
      showCustomAlert.error(e?.response?.data?.message || 'Greška pri učitavanju detalja');
      setDetailVisible(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const totalWorkers = data?.reports?.length || 0;
  const daysInMonth = data?.period?.daysInMonth || new Date(year, month, 0).getDate();

  const workerOptions = useMemo(() => {
    return workers.map((w) => ({
      label: `${w.lastName} ${w.firstName}`,
      value: w._id
    }));
  }, [workers]);

  const radniDaniCell = (row: MonthlyAllResponse['reports'][number]) => {
    const s = row.summary;
    const totalDays =
      s.totalWorkDays +
      s.totalSickLeaveDays +
      s.totalAnnualLeaveDays +
      s.totalPaidLeaveDays +
      s.totalUnpaidLeaveDays +
      s.totalHolidayDays +
      s.totalSlavaDays;

    return (
      <div>
        <div style={{ fontWeight: 700 }}>{s.totalWorkDays} dan{ s.totalWorkDays === 1 ? '' : 'a'}</div>
        <div className="flex gap-2 flex-wrap mt-1">
          {s.totalAnnualLeaveDays > 0 && (
            <span className="inline-flex px-2 py-1 text-xs font-semibold border-round bg-cyan-500 text-white">
              {s.totalAnnualLeaveDays} godišnji
            </span>
          )}
          {s.totalSickLeaveDays > 0 && (
            <span className="inline-flex px-2 py-1 text-xs font-semibold border-round bg-red-500 text-white">
              {s.totalSickLeaveDays} bolovanje
            </span>
          )}
          {s.totalPaidLeaveDays > 0 && (
            <span className="inline-flex px-2 py-1 text-xs font-semibold border-round bg-purple-500 text-white">
              {s.totalPaidLeaveDays} plaćeno
            </span>
          )}
          {s.totalUnpaidLeaveDays > 0 && (
            <span className="inline-flex px-2 py-1 text-xs font-semibold border-round bg-orange-500 text-white">
              {s.totalUnpaidLeaveDays} neplaćeno
            </span>
          )}
        </div>
        <div className="text-600 mt-1">Ukupno: {totalDays} / {daysInMonth} dana</div>
      </div>
    );
  };

  const satiRadaCell = (row: MonthlyAllResponse['reports'][number]) => {
    const s = row.summary;
    const baseHours = Math.max(0, (s.totalHoursWorked || 0) - (s.totalOvertimeHours || 0));
    return (
      <div>
        <div style={{ fontWeight: 700 }}>{(s.totalHoursWorked || 0).toFixed(1)} h</div>
        <div className="text-600">Osnovno: {baseHours.toFixed(1)}h</div>
      </div>
    );
  };

  const statusCell = (row: MonthlyAllResponse['reports'][number]) => {
    const active = row.worker.isActive !== false;
    return (
      <span className={`inline-flex px-3 py-1 text-sm border-round ${active ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'}`}>
        {active ? 'Aktivan' : 'Neaktivan'}
      </span>
    );
  };

  const actionsCell = (row: MonthlyAllResponse['reports'][number]) => {
    return (
      <Button
        icon="pi pi-eye"
        rounded
        severity="info"
        tooltip="Detalji"
        tooltipOptions={{ position: 'left' }}
        onClick={() => openDetails(row.worker._id)}
      />
    );
  };

  const exportMonthlyCsv = () => {
    if (!data) return;
    const lines: string[] = [];
    lines.push(
      [
        'Radnik',
        'Pozicija',
        'RadniDani',
        'Bolovanje',
        'Godisnji',
        'Placeno',
        'Neplaceno',
        'Praznici',
        'Slava',
        'UkupnoSati',
        'Prekovremeni'
      ].join(',')
    );
    data.reports.forEach((r) => {
      const s = r.summary;
      lines.push(
        [
          csvEscape(`${r.worker.lastName} ${r.worker.firstName}`),
          csvEscape(r.worker.position),
          s.totalWorkDays,
          s.totalSickLeaveDays,
          s.totalAnnualLeaveDays,
          s.totalPaidLeaveDays,
          s.totalUnpaidLeaveDays,
          s.totalHolidayDays,
          s.totalSlavaDays,
          (s.totalHoursWorked || 0).toFixed(1),
          (s.totalOvertimeHours || 0).toFixed(1)
        ].join(',')
      );
    });
    downloadTextFile(`mesecni_izvestaj_${year}_${String(month).padStart(2, '0')}.csv`, lines.join('\n'));
  };

  const exportMonthlyPdf = () => {
    if (!data) return;
    const title = `Mesečni izveštaji - ${monthOptions.find((m) => m.value === month)?.label || month} ${year}`;
    const html = `
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${title}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 16px; }
            h2 { margin: 0 0 12px 0; }
            table { width: 100%; border-collapse: collapse; margin-top: 12px; }
            th, td { border: 1px solid #ddd; padding: 8px; font-size: 12px; }
            th { background: #f5f5f5; text-align: left; }
          </style>
        </head>
        <body>
          <h2>${title}</h2>
          <div>Ukupno radnika: ${totalWorkers} | Broj dana u mesecu: ${daysInMonth}</div>
          <table>
            <thead>
              <tr>
                <th>Radnik</th>
                <th>Pozicija</th>
                <th>Radni dani</th>
                <th>Bolovanje</th>
                <th>Godišnji</th>
                <th>Plaćeno</th>
                <th>Neplaćeno</th>
                <th>Ukupno sati</th>
                <th>Prekovremeni</th>
              </tr>
            </thead>
            <tbody>
              ${data.reports
                .map((r) => {
                  const s = r.summary;
                  return `<tr>
                    <td>${r.worker.lastName} ${r.worker.firstName}</td>
                    <td>${r.worker.position}</td>
                    <td>${s.totalWorkDays}</td>
                    <td>${s.totalSickLeaveDays}</td>
                    <td>${s.totalAnnualLeaveDays}</td>
                    <td>${s.totalPaidLeaveDays}</td>
                    <td>${s.totalUnpaidLeaveDays}</td>
                    <td>${(s.totalHoursWorked || 0).toFixed(1)}</td>
                    <td>${(s.totalOvertimeHours || 0).toFixed(1)}</td>
                  </tr>`;
                })
                .join('')}
            </tbody>
          </table>
          <script>
            window.onload = () => { window.print(); };
          </script>
        </body>
      </html>
    `;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  // Advanced export (CSV) for monthly or custom range
  const computeRangeSummary = async (from: Date, to: Date, selected: Worker[]) => {
    const fromD = new Date(from);
    const toD = new Date(to);
    fromD.setHours(0, 0, 0, 0);
    toD.setHours(23, 59, 59, 999);

    const allAbsences = (await absenceAPI.getAll({ isActive: true })) as Absence[];
    const holidays = allAbsences.filter((a) => a.category === 'holiday' && a.isActive);

    // Precompute holiday set for range (recurring)
    const holidaySet = new Set<string>();
    const cur = new Date(fromD);
    while (cur <= toD) {
      const m = cur.getMonth();
      const d = cur.getDate();
      // check recurring holiday ranges
      for (const h of holidays) {
        if (h.isRecurring && h.recurringMonth !== undefined && h.recurringDay !== undefined) {
          const startM = h.recurringMonth;
          const startD = h.recurringDay;
          const endM = h.recurringEndMonth ?? startM;
          const endD = h.recurringEndDay ?? startD;
          const key = m * 100 + d;
          const startKey = startM * 100 + startD;
          const endKey = endM * 100 + endD;
          if (key >= startKey && key <= endKey) {
            holidaySet.add(toYmd(cur));
            break;
          }
        } else {
          // non-recurring: fallback to date range
          const hs = new Date(h.startDate);
          const he = new Date(h.endDate);
          hs.setHours(0, 0, 0, 0);
          he.setHours(23, 59, 59, 999);
          if (cur >= hs && cur <= he) {
            holidaySet.add(toYmd(cur));
            break;
          }
        }
      }
      cur.setDate(cur.getDate() + 1);
    }

    const results: Array<{ worker: Worker; summary: any }> = [];

    for (const w of selected) {
      const [records, sickYear, annualYear, paidYear] = await Promise.all([
        attendanceAPI.getAttendanceRecordsForWorker({ workerId: w._id, startDate: fromD.toISOString(), endDate: toD.toISOString() }),
        attendanceAPI.getSickLeaveHistoryYear(w._id, fromD.getFullYear()),
        attendanceAPI.getAnnualLeaveHistoryYear(w._id, fromD.getFullYear()),
        attendanceAPI.getPaidUnpaidLeaveHistoryYear(w._id, fromD.getFullYear())
      ]);

      const recordMap = new Map<string, any>();
      records.forEach((r: any) => recordMap.set(toYmd(new Date(r.date)), r));

      const sickSet = new Set<string>();
      sickYear.forEach((s: any) => {
        const sStart = new Date(s.startDate);
        const sEnd = s.endDate ? new Date(s.endDate) : toD;
        const d = new Date(sStart);
        d.setHours(0, 0, 0, 0);
        sEnd.setHours(23, 59, 59, 999);
        while (d <= sEnd) {
          const dow = d.getDay();
          if (s.excludeWeekends && (dow === 0 || dow === 6)) {
            d.setDate(d.getDate() + 1);
            continue;
          }
          const key = toYmd(d);
          if (d >= fromD && d <= toD) sickSet.add(key);
          d.setDate(d.getDate() + 1);
        }
      });

      const annualSet = new Set<string>();
      annualYear.forEach((a: any) => {
        const sStart = new Date(a.startDate);
        const sEnd = new Date(a.endDate);
        const d = new Date(sStart);
        d.setHours(0, 0, 0, 0);
        sEnd.setHours(23, 59, 59, 999);
        while (d <= sEnd) {
          if (d >= fromD && d <= toD) annualSet.add(toYmd(d));
          d.setDate(d.getDate() + 1);
        }
      });

      const paidSet = new Set<string>();
      const unpaidSet = new Set<string>();
      paidYear.forEach((p: any) => {
        const sStart = new Date(p.startDate);
        const sEnd = new Date(p.endDate);
        const d = new Date(sStart);
        d.setHours(0, 0, 0, 0);
        sEnd.setHours(23, 59, 59, 999);
        while (d <= sEnd) {
          const dow = d.getDay();
          if (p.excludeWeekends && (dow === 0 || dow === 6)) {
            d.setDate(d.getDate() + 1);
            continue;
          }
          if (d >= fromD && d <= toD) {
            (p.leaveType === 'paid' ? paidSet : unpaidSet).add(toYmd(d));
          }
          d.setDate(d.getDate() + 1);
        }
      });

      // slava (recurring one day)
      const slavaSet = new Set<string>();
      if (w.slavaMonth !== undefined && w.slavaDay !== undefined && w.slavaMonth !== null && w.slavaDay !== null) {
        const d = new Date(fromD.getFullYear(), w.slavaMonth, w.slavaDay);
        const key = toYmd(d);
        if (d >= fromD && d <= toD) slavaSet.add(key);
      }

      // Walk days and compute
      let totalWorkDays = 0;
      let totalSickLeaveDays = 0;
      let totalAnnualLeaveDays = 0;
      let totalPaidLeaveDays = 0;
      let totalUnpaidLeaveDays = 0;
      let totalHolidayDays = 0;
      let totalSlavaDays = 0;
      let totalHoursWorked = 0;
      let totalOvertimeHours = 0;

      const d = new Date(fromD);
      while (d <= toD) {
        const key = toYmd(d);
        if (sickSet.has(key)) {
          totalSickLeaveDays++;
          totalHoursWorked += 8;
        } else if (annualSet.has(key)) {
          totalAnnualLeaveDays++;
          totalHoursWorked += 8;
        } else if (paidSet.has(key)) {
          totalPaidLeaveDays++;
          totalHoursWorked += 8;
        } else if (unpaidSet.has(key)) {
          totalUnpaidLeaveDays++;
        } else if (holidaySet.has(key)) {
          totalHolidayDays++;
          totalHoursWorked += 8;
        } else if (slavaSet.has(key)) {
          totalSlavaDays++;
          totalWorkDays++;
          totalHoursWorked += 8;
        } else {
          const rec = recordMap.get(key);
          if (rec && rec.hoursWorked > 0) {
            totalWorkDays++;
            totalHoursWorked += rec.hoursWorked;
            totalOvertimeHours += rec.overtimeHours || 0;
          }
        }
        d.setDate(d.getDate() + 1);
      }

      results.push({
        worker: w,
        summary: {
          totalWorkDays,
          totalSickLeaveDays,
          totalAnnualLeaveDays,
          totalPaidLeaveDays,
          totalUnpaidLeaveDays,
          totalHolidayDays,
          totalSlavaDays,
          totalHoursWorked,
          totalOvertimeHours
        }
      });
    }

    return results;
  };

  const doAdvancedExport = async () => {
    try {
      setExportError('');
      const typeSet = new Set(exportTypes);
      if (typeSet.has('all')) {
        // ok
      } else if (typeSet.size === 0) {
        setExportError('Izaberite tip podataka.');
        return;
      }

      let selected = workers;
      if (!allWorkers) {
        if (selectedWorkers.length === 0) {
          setExportError('Izaberite radnike ili čekirajte "Svi radnici".');
          return;
        }
        selected = workers.filter((w) => selectedWorkers.includes(w._id));
      }

      if (periodType === 'monthly') {
        if (!data) {
          setExportError('Nema podataka za izabrani mesec.');
          return;
        }
        const map = new Map<string, MonthlyAllResponse['reports'][number]['summary']>();
        data.reports.forEach((r) => map.set(r.worker._id, r.summary));

        const lines: string[] = [];
        const cols = ['Radnik', 'Pozicija'];
        const add = (key: string, label: string) => {
          if (typeSet.has('all') || typeSet.has(key)) cols.push(label);
        };
        add('work', 'RadniDani');
        add('sick', 'Bolovanje');
        add('annual', 'Godisnji');
        add('paid', 'Placeno');
        add('unpaid', 'Neplaceno');
        add('holiday', 'Praznici');
        add('slava', 'Slava');
        add('overtime', 'Prekovremeni');
        cols.push('UkupnoSati');
        lines.push(cols.join(','));

        selected.forEach((w) => {
          const s = map.get(w._id) || {
            totalWorkDays: 0,
            totalSickLeaveDays: 0,
            totalAnnualLeaveDays: 0,
            totalPaidLeaveDays: 0,
            totalUnpaidLeaveDays: 0,
            totalHolidayDays: 0,
            totalSlavaDays: 0,
            totalHoursWorked: 0,
            totalOvertimeHours: 0
          };
          const row: any[] = [csvEscape(`${w.lastName} ${w.firstName}`), csvEscape(typeof w.positionId === 'object' && w.positionId ? (w.positionId as any).name : '')];
          const pushIf = (key: string, val: any) => {
            if (typeSet.has('all') || typeSet.has(key)) row.push(val);
          };
          pushIf('work', s.totalWorkDays);
          pushIf('sick', s.totalSickLeaveDays);
          pushIf('annual', s.totalAnnualLeaveDays);
          pushIf('paid', s.totalPaidLeaveDays);
          pushIf('unpaid', s.totalUnpaidLeaveDays);
          pushIf('holiday', s.totalHolidayDays);
          pushIf('slava', s.totalSlavaDays);
          pushIf('overtime', (s.totalOvertimeHours || 0).toFixed(1));
          row.push((s.totalHoursWorked || 0).toFixed(1));
          lines.push(row.join(','));
        });

        downloadTextFile(`napredni_export_${year}_${String(month).padStart(2, '0')}.csv`, lines.join('\n'));
        setAdvancedVisible(false);
        return;
      }

      // Custom period
      if (!customFrom || !customTo) {
        setExportError('Unesite period.');
        return;
      }
      if (customTo < customFrom) {
        setExportError('Datum do mora biti nakon datuma od.');
        return;
      }

      const summaries = await computeRangeSummary(customFrom, customTo, selected);
      const lines: string[] = [];
      const cols = ['Radnik', 'Pozicija'];
      const add = (key: string, label: string) => {
        if (typeSet.has('all') || typeSet.has(key)) cols.push(label);
      };
      add('work', 'RadniDani');
      add('sick', 'Bolovanje');
      add('annual', 'Godisnji');
      add('paid', 'Placeno');
      add('unpaid', 'Neplaceno');
      add('holiday', 'Praznici');
      add('slava', 'Slava');
      add('overtime', 'Prekovremeni');
      cols.push('UkupnoSati');
      lines.push(cols.join(','));

      summaries.forEach(({ worker, summary }) => {
        const row: any[] = [
          csvEscape(`${worker.lastName} ${worker.firstName}`),
          csvEscape(typeof worker.positionId === 'object' && worker.positionId ? (worker.positionId as any).name : '')
        ];
        const pushIf = (key: string, val: any) => {
          if (typeSet.has('all') || typeSet.has(key)) row.push(val);
        };
        pushIf('work', summary.totalWorkDays);
        pushIf('sick', summary.totalSickLeaveDays);
        pushIf('annual', summary.totalAnnualLeaveDays);
        pushIf('paid', summary.totalPaidLeaveDays);
        pushIf('unpaid', summary.totalUnpaidLeaveDays);
        pushIf('holiday', summary.totalHolidayDays);
        pushIf('slava', summary.totalSlavaDays);
        pushIf('overtime', (summary.totalOvertimeHours || 0).toFixed(1));
        row.push((summary.totalHoursWorked || 0).toFixed(1));
        lines.push(row.join(','));
      });

      downloadTextFile(`napredni_export_${toYmd(customFrom)}_${toYmd(customTo)}.csv`, lines.join('\n'));
      setAdvancedVisible(false);
    } catch (e: any) {
      console.error('Advanced export error:', e);
      setExportError(e?.response?.data?.message || 'Greška pri exportu');
    }
  };

  const detailStatusBadge = (r: any) => {
    if (r.isAnnualLeave) return <span className="inline-flex px-2 py-1 text-xs font-semibold border-round bg-cyan-500 text-white">Godišnji</span>;
    if (r.isSickLeave) return <span className="inline-flex px-2 py-1 text-xs font-semibold border-round bg-red-500 text-white">Bolovanje</span>;
    if (r.isSlava) return <span className="inline-flex px-2 py-1 text-xs font-semibold border-round bg-pink-500 text-white">Slava</span>;
    if (r.hoursWorked > 0) return <span className="inline-flex px-2 py-1 text-xs font-semibold border-round bg-green-500 text-white">Radio</span>;
    return <span className="inline-flex px-2 py-1 text-xs font-semibold border-round bg-gray-500 text-white">—</span>;
  };

  return (
    <div className="p-4">
      <Card>
        <div className="flex justify-content-between align-items-center mb-3 flex-wrap gap-3">
          <h2 className="m-0">Mesečni izveštaji</h2>

          <div className="flex gap-2 flex-wrap">
            <Button label="Osveži" icon="pi pi-refresh" severity="secondary" onClick={refresh} />
            <Button label="Eksportuj CSV" icon="pi pi-download" severity="info" onClick={exportMonthlyCsv} />
            <Button label="Eksportuj PDF" icon="pi pi-file-pdf" severity="info" onClick={exportMonthlyPdf} />
            <Button label="Napredni Export" icon="pi pi-cog" severity="help" onClick={() => setAdvancedVisible(true)} />
          </div>
        </div>

        <div className="flex align-items-center gap-3 mb-3 flex-wrap">
          <div className="flex align-items-center gap-2">
            <div className="font-semibold">Mesec:</div>
            <Dropdown value={month} options={monthOptions} onChange={(e) => setMonth(e.value)} appendTo="self" />
          </div>
          <div className="flex align-items-center gap-2">
            <div className="font-semibold">Godina:</div>
            <Dropdown value={year} options={yearOptions} onChange={(e) => setYear(e.value)} appendTo="self" />
          </div>
        </div>

        <div className="text-600 mb-3">
          <div className="font-semibold">{`Pregled za ${monthOptions.find((m) => m.value === month)?.label || month} ${year}`}</div>
          <div>{`Ukupno radnika: ${totalWorkers} | Broj dana u mesecu: ${daysInMonth}`}</div>
        </div>

        <DataTable
          value={data?.reports || []}
          loading={loading}
          paginator
          rows={15}
          rowsPerPageOptions={[15, 25, 50]}
          currentPageReportTemplate="Prikaz {first} do {last} od ukupno {totalRecords} radnika"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          emptyMessage="Nema podataka"
          className="p-datatable-sm"
          responsiveLayout="stack"
          breakpoint="768px"
        >
          <Column
            header="Radnik"
            sortable
            body={(r: any) => <span style={{ fontWeight: 600 }}>{`${r.worker.lastName} ${r.worker.firstName}`}</span>}
          />
          <Column header="Pozicija" sortable body={(r: any) => r.worker.position} />
          <Column header="Radni dani / Odsustva" body={radniDaniCell} />
          <Column header="Sati rada" body={satiRadaCell} />
          <Column header="Status" body={statusCell} />
          <Column header="" body={actionsCell} style={{ width: '90px' }} />
        </DataTable>
      </Card>

      <Dialog
        header={detail ? `Detaljan izveštaj - ${detail.worker.lastName} ${detail.worker.firstName}` : 'Detaljan izveštaj'}
        visible={detailVisible}
        onHide={() => setDetailVisible(false)}
        style={{ width: '820px' }}
        draggable={false}
      >
        {detailLoading && <div className="text-600">Učitavanje...</div>}
        {!detailLoading && !detail && <Message severity="warn" text="Nema podataka" />}
        {!detailLoading && detail && (
          <div className="grid">
            <div className="col-12">
              <div className="grid">
                <div className="col-12 md:col-6">
                  <Card>
                    <h3 className="m-0 mb-3">Osnovni podaci</h3>
                    <div><b>Pozicija:</b> {detail.worker.position}</div>
                  </Card>
                </div>
                <div className="col-12 md:col-6">
                  <Card>
                    <h3 className="m-0 mb-3">Sumarna statistika</h3>
                    <div><b>Radni dani:</b> {detail.summary.totalWorkDays}</div>
                    <div><b>Bolovanje:</b> {detail.summary.totalSickLeaveDays} dana</div>
                    <div><b>Godišnji odmor:</b> {detail.summary.totalAnnualLeaveDays} dana</div>
                    <div><b>Ukupno sati rada:</b> {(detail.summary.totalHoursWorked || 0).toFixed(1)} h</div>
                    <div><b>Prekovremeni sati:</b> {(detail.summary.totalOvertimeHours || 0).toFixed(1)} h</div>
                  </Card>
                </div>
              </div>
            </div>

            <div className="col-12">
              <Card>
                <h3 className="m-0 mb-3">Godišnji odmori</h3>
                <DataTable value={detail.annualLeaves || []} emptyMessage="Nema evidencije godišnjih odmora" className="p-datatable-sm" responsiveLayout="stack" breakpoint="768px">
                  <Column header="Od" body={(r: any) => formatDateSerbian(r.startDate)} />
                  <Column header="Do" body={(r: any) => formatDateSerbian(r.endDate)} />
                  <Column header="Broj dana" field="numberOfDays" />
                  <Column header="Napomene" field="notes" />
                </DataTable>
              </Card>
            </div>

            <div className="col-12">
              <Card>
                <h3 className="m-0 mb-3">Dnevna evidencija</h3>
                <DataTable value={detail.records || []} emptyMessage="Nema evidencije" className="p-datatable-sm" responsiveLayout="stack" breakpoint="768px">
                  <Column header="Datum" body={(r: any) => formatDateSerbian(r.date)} />
                  <Column header="Sati rada" body={(r: any) => `${(r.hoursWorked || 0).toFixed(1)} h`} />
                  <Column header="Prekovremeno" body={(r: any) => ((r.overtimeHours || 0) > 0 ? `${r.overtimeHours.toFixed(1)} h` : '—')} />
                  <Column header="Status" body={detailStatusBadge} />
                  <Column header="Napomene" field="notes" />
                </DataTable>
              </Card>
            </div>
          </div>
        )}
      </Dialog>

      <Dialog
        header="Napredni Export"
        visible={advancedVisible}
        onHide={() => setAdvancedVisible(false)}
        style={{ width: '620px' }}
        draggable={false}
        className="p-fluid"
      >
        {exportError && <Message severity="error" text={exportError} className="mb-3" />}

        <div className="field">
          <label className="block mb-2">
            Tip perioda <span className="text-red-500">*</span>
          </label>
          <Dropdown
            value={periodType}
            options={[
              { label: 'Mesečni', value: 'monthly' },
              { label: 'Custom period', value: 'custom' }
            ]}
            onChange={(e) => setPeriodType(e.value)}
            appendTo="self"
          />
        </div>

        {periodType === 'custom' && (
          <>
            <div className="field">
              <label className="block mb-2">
                Datum od <span className="text-red-500">*</span>
              </label>
              <Calendar value={customFrom} onChange={(e) => setCustomFrom(e.value as Date)} dateFormat="dd.mm.yy" showIcon />
            </div>
            <div className="field">
              <label className="block mb-2">
                Datum do <span className="text-red-500">*</span>
              </label>
              <Calendar value={customTo} onChange={(e) => setCustomTo(e.value as Date)} dateFormat="dd.mm.yy" showIcon />
            </div>
          </>
        )}

        <div className="field">
          <label className="block mb-2">
            Tip podataka <span className="text-red-500">*</span>
          </label>
          <MultiSelect
            value={exportTypes}
            options={dataTypeOptions}
            onChange={(e) => {
              const vals: string[] = e.value || [];
              // If "all" selected, keep only it
              if (vals.includes('all')) setExportTypes(['all']);
              else setExportTypes(vals);
            }}
            placeholder="Izaberite tip podataka"
            display="chip"
            appendTo="self"
          />
        </div>

        <div className="field flex align-items-center gap-2">
          <Checkbox inputId="all-workers" checked={allWorkers} onChange={(e) => setAllWorkers(!!e.checked)} />
          <label htmlFor="all-workers">Svi radnici</label>
        </div>

        {!allWorkers && (
          <div className="field">
            <label className="block mb-2">
              Izaberi radnike <span className="text-red-500">*</span>
            </label>
            <MultiSelect
              value={selectedWorkers}
              options={workerOptions}
              onChange={(e) => setSelectedWorkers(e.value || [])}
              placeholder="Izaberite radnike"
              display="chip"
              appendTo="self"
            />
          </div>
        )}

        <div className="flex justify-content-end gap-2 mt-3">
          <Button label="Otkaži" className="p-button-secondary p-button-outlined" onClick={() => setAdvancedVisible(false)} />
          <Button label="Eksportuj" icon="pi pi-file" severity="info" onClick={doAdvancedExport} />
        </div>
      </Dialog>
    </div>
  );
};

export default AttendanceReports;


